import { useCallback, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslations } from "@/lib/translations";
import { RefreshCw } from "lucide-react";

interface CaptchaResponse {
  question: string;
  token: string;
}

interface CaptchaFieldProps {
  token: string;
  onTokenChange: (token: string) => void;
  answer: string;
  onAnswerChange: (answer: string) => void;
  disabled?: boolean;
}

export function CaptchaField({
  token,
  onTokenChange,
  answer,
  onAnswerChange,
  disabled,
}: CaptchaFieldProps) {
  const { t, isRTL } = useTranslations();
  const [question, setQuestion] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);

  const fetchChallenge = useCallback(async () => {
    setLoading(true);
    setErrored(false);
    try {
      const res = await fetch("/api/captcha", { cache: "no-store" });
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as CaptchaResponse;
      setQuestion(data.question);
      onTokenChange(data.token);
      onAnswerChange("");
    } catch {
      setErrored(true);
      setQuestion("");
      onTokenChange("");
    } finally {
      setLoading(false);
    }
  }, [onAnswerChange, onTokenChange]);

  useEffect(() => {
    if (!token) void fetchChallenge();
  }, [token, fetchChallenge]);

  return (
    <div className="space-y-2">
      <Label htmlFor="lead-captcha">
        {t("lead.form.captcha")}{" "}
        <span className="text-destructive">*</span>
      </Label>
      <div className="flex items-center gap-2">
        <div
          className="flex-1 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm font-mono tracking-wider select-none"
          aria-live="polite"
          data-testid="lead-captcha-question"
          dir="ltr"
        >
          {loading
            ? "…"
            : errored
              ? t("lead.form.captchaError")
              : question
                ? `${question} = ?`
                : ""}
        </div>
        <Input
          id="lead-captcha"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={answer}
          onChange={(e) => onAnswerChange(e.target.value)}
          required
          maxLength={4}
          className="w-20"
          disabled={disabled || loading || errored}
          data-testid="input-lead-captcha"
          dir="ltr"
        />
        <button
          type="button"
          onClick={() => void fetchChallenge()}
          className="text-muted-foreground hover:text-foreground p-2 rounded-md"
          aria-label={t("lead.form.captchaReload")}
          disabled={disabled || loading}
          data-testid="button-lead-captcha-reload"
        >
          <RefreshCw className={`h-4 w-4 ${isRTL ? "ml-0" : ""}`} />
        </button>
      </div>
    </div>
  );
}

export default CaptchaField;
